import type { Dict } from '@/types/dict'
import {
    isRef,
  ref,
  shallowRef,
  watchEffect,
  type Ref,
  type ShallowRef
} from 'vue'
import { useEffect } from './useEffect'

type Primitive = number | string | undefined | null
type Arr<T = any> = Array<T>

export type MaybeShallowRef<T> =  T | ShallowRef<T>

export const isShallowRef = <T>(x: any): x is ShallowRef<T> => isRef(x)

export type DeepRef<T extends Dict | Arr> = {
  [p in keyof T]: T[p] extends Primitive
    ? Ref<T[p]>
    : T[p] extends Dict
      ? Ref<DeepRef<T[p]>>
      : T[p] extends Arr
        ? Ref<DeepRef<T[p]>>
        : T[p]
}

export const deepRef = <T extends Dict>(obj: T): DeepRef<T> => {
  return Object.keys(obj).reduce((prev, cur) => {
    const val = obj[cur]
    return {
      ...prev,
      [cur]:
        typeof val === 'string' ||
        typeof val === 'number' ||
        typeof val === 'undefined' ||
        val === null
          ? ref(val)
          : Array.isArray(val)
            ? ref(val.map((it) => deepRef(it)))
            : typeof val === 'object'
              ? ref(deepRef(val))
              : val
    }
  }, {} as DeepRef<T>)
}

export type ArrayRef<T> = T extends Ref ? Ref<Array<T>> : Ref<Array<Ref<T>>>
export type ShallowArrayRef<T> = T extends ShallowRef ? Ref<Array<T>> : Ref<Array<ShallowRef<T>>>

export const arrayRef = <T>(arr: T[]): ArrayRef<T> => {
  return ref(arr.map((it) => ref<T>(it))) as unknown as any as ArrayRef<T>
}

export const shallowArrayRef = <T>(arr: T[]): ShallowArrayRef<T> => {
  return ref(arr.map((it) => shallowRef<T>(it))) as unknown as any as ShallowArrayRef<T>
}

export type ComputedOptions = {
  deps?: any[]
}

export const computedArrayRef = <T>(
  fun: () => T[],
  options?: ComputedOptions
): ArrayRef<T> => {
  const arr = arrayRef<T>([])

  const update = () => {
    const next = fun()

    arr.value = []
    for (let i = 0; i < next.length; i++) {
      arr.value.push(ref<T>(next[i]) as any)
    }
  }

  if (options?.deps) {
    useEffect(() => {
      update()
    }, options.deps)
  } else {
    watchEffect(() => {
      update()
    })
  }

  return arr
}

export const computedShallowArrayRef = <T>(
  fun: () => T[],
  options?: ComputedOptions
): ShallowArrayRef<T> => {
  const arr = shallowArrayRef<T>([])

  const update = () => {
    const next = fun()
    arr.value = []
    for (let i = 0; i < next.length; i++) {
      arr.value.push(shallowRef(next[i]) as any)
    }
  }

  if (options?.deps) {
    useEffect(() => {
      update()
    }, options.deps)
  } else {
    watchEffect(() => {
      update()
    })
  }

  return arr
}

export const computedShallow = <T>(
  fun: () => T,
  options?: ComputedOptions
): ShallowRef<T> => {
  const data = shallowRef<T>(fun());
  
  const update = () => {
    const next = fun();
    data.value = next;
  }

  if (options?.deps) {
    useEffect(() => {
      update()
    }, options.deps)
  } else {
    watchEffect(() => {
      update()
    })
  }

  return data
}

export type NormalizedRef<T extends Dict | Arr | Primitive | Ref> = {
  [prop in keyof T]: T[prop] extends Primitive
    ? T[prop]
    : T[prop] extends Ref
      ? NormalizedRef<T[prop]['value']>
      : T[prop] extends Dict | Arr
        ? NormalizedRef<T[prop]>
        : T[prop] //T[prop] extends Ref ? T[prop]['value']
}
